import { clusterByLCP } from '../lcp-clustering';
import { multiLCS } from '../longest-common-sequence';

import { endOfHighlightMark, startOfHighlightMark } from './start-end-markers';

const TOKEN_REGEX = /[^.=:\\/-]+|./u;
const ELLIPSIS = '…';

function tokenize(s: string): string[] {
  return [...s.matchAll(RegExp(TOKEN_REGEX, 'gu'))].map(([match]) => match);
}

function findCommonIndices(
  tokens: string[][],
  commonSubseq: string[],
): {
  commonIndices: number[][];
  gapHasDiff: boolean[];
} {
  const commonIndices: number[][] = tokens.map(() => new Array(commonSubseq.length).fill(0));
  const gapHasDiff: boolean[] = new Array(commonSubseq.length + 1).fill(false);

  tokens.forEach((singleTokens, tokensIdx) => {
    const indices = commonIndices[tokensIdx];
    let prevCommonIdx = -1;

    for (let i = 0; i < commonSubseq.length; ++i) {
      const commonIdx = singleTokens.indexOf(commonSubseq[i], prevCommonIdx + 1);
      indices[i] = commonIdx;
      gapHasDiff[i] ||= commonIdx !== prevCommonIdx + 1;

      prevCommonIdx = commonIdx;
    }

    gapHasDiff[commonSubseq.length] ||= singleTokens.length !== prevCommonIdx + 1;
  });

  return {
    commonIndices,
    gapHasDiff,
  };
}

function highlightDifferences(
  tokens: string[],
  commonIndices: number[],
  gapHasDiff: boolean[],
  contextDisplay: 'full' | 'shortened',
): string {
  const resultArr: string[] = [];
  let commonStreakEnd = 0;
  let commonStreakStart = 0;

  commonIndices.forEach((commonIdx, i) => {
    if (gapHasDiff[i]) {
      appendContext(
        resultArr,
        tokens,
        commonStreakStart,
        commonStreakEnd,
        { diffBefore: commonStreakStart !== 0, diffAfter: true },
        contextDisplay,
      );
      appendDiff(resultArr, tokens, commonStreakEnd, commonIdx);

      commonStreakStart = commonIdx;
    }

    commonStreakEnd = commonIdx + 1;
  });

  const diffAtTheEnd = gapHasDiff[commonIndices.length];

  appendContext(
    resultArr,
    tokens,
    commonStreakStart,
    commonStreakEnd,
    { diffBefore: commonStreakStart !== 0, diffAfter: diffAtTheEnd },
    contextDisplay,
  );

  if (diffAtTheEnd) {
    appendDiff(resultArr, tokens, commonStreakEnd, tokens.length);
  }

  return resultArr.join('');
}

function appendDiff(output: string[], tokens: string[], start: number, end: number) {
  output.push(startOfHighlightMark);
  output.push(...tokens.slice(start, end));
  output.push(endOfHighlightMark);
}

const CONTEXT_TOKENS_AFTER_DIFF = 0;
const CONTEXT_TOKENS_BEFORE_DIFF = 2;

function appendContext(
  output: string[],
  tokens: string[],
  start: number,
  end: number,
  { diffBefore, diffAfter }: { diffBefore: boolean; diffAfter: boolean },
  contextDisplay: 'full' | 'shortened',
) {
  if (contextDisplay === 'full') {
    output.push(...tokens.slice(start, end));
    return;
  }

  const contextTokensCount = end - start;
  const startTokensToShow = diffBefore
    ? Math.min(contextTokensCount, CONTEXT_TOKENS_AFTER_DIFF)
    : 0;
  const endTokensToShow = diffAfter ? Math.min(contextTokensCount, CONTEXT_TOKENS_BEFORE_DIFF) : 0;

  if (
    startTokensToShow + endTokensToShow >= contextTokensCount ||
    (startTokensToShow + endTokensToShow + 1 === contextTokensCount &&
      tokens[start + startTokensToShow].length === 1)
  ) {
    output.push(...tokens.slice(start, end));
  } else {
    output.push(...tokens.slice(start, start + startTokensToShow));
    output.push(ELLIPSIS);
    output.push(...tokens.slice(end - endTokensToShow, end));
  }
}

export function preprocessWithLcs(inputStrings: string[] | string[][]): {
  tokens: string[][];
  commonTokens: string[];
  commonIndices: number[][];
  gapHasDiff: boolean[];
} {
  let tokens: string[][];

  if (Array.isArray(inputStrings[0])) {
    tokens = inputStrings as string[][];
  } else {
    tokens = (inputStrings as string[]).map(tokenize);
  }

  const commonTokens = multiLCS(tokens);

  const { commonIndices, gapHasDiff } = findCommonIndices(tokens, commonTokens);

  return { tokens, commonTokens, commonIndices, gapHasDiff };
}

function allStringEqual(strings: string[]) {
  return strings.every((s) => s === strings[0]);
}

export const DIFF_MARKER = Symbol();
export type DIFF_MARKER = typeof DIFF_MARKER;

/**
 * Compiles the extra outputs from highlightDiffs to get a single array representing the common parts
 * with diffs marked with a special value.
 */
function getCommonWithDiffMarkers(
  common: string[],
  gapHasDiff: boolean[],
): Array<string | DIFF_MARKER> {
  let commonStreakStart = 0;
  const result: Array<string | DIFF_MARKER> = [];

  for (let i = 0; i <= common.length; ++i) {
    if (gapHasDiff[i]) {
      if (i !== 0) {
        result.push(common.slice(commonStreakStart, i).join(''));
      }

      result.push(DIFF_MARKER);

      commonStreakStart = i;
    }
  }

  if (commonStreakStart < common.length) {
    result.push(common.slice(commonStreakStart, common.length).join(''));
  }

  return result;
}

export function findCommonPartsAndDiffs(
  inputs: (string | null | undefined)[],
): Array<string | DIFF_MARKER> {
  if (!inputs.length) {
    return [];
  }

  const inputStrings = inputs.map((input) => input ?? '');

  if (allStringEqual(inputStrings)) {
    return [inputStrings[0]];
  }

  const { commonTokens, gapHasDiff } = preprocessWithLcs(inputStrings);

  return getCommonWithDiffMarkers(commonTokens, gapHasDiff);
}

/**
 * Main processing function for a list of metrics.
 * Returns various representations of the metrics with differences highlighted.
 */
export function highlightDiffs(inputs: (string | null | undefined)[]): {
  full: string[];
  shortened: string[];
};
export function highlightDiffs(
  inputs: (string | null | undefined)[],
  context: 'shortened',
): string[];
export function highlightDiffs(inputs: (string | null | undefined)[], context: 'full'): string[];

export function highlightDiffs(
  inputs: (string | null | undefined)[],
  context?: 'shortened' | 'full',
): { shortened: string[]; full: string[] } | string[] {
  const inputStrings = inputs.map((input) => input ?? '');

  if (allStringEqual(inputStrings)) {
    if (context) {
      return inputStrings;
    }

    return {
      shortened: inputStrings,
      full: inputStrings,
    };
  }

  const { commonIndices, gapHasDiff, tokens } = preprocessWithLcs(inputStrings);

  if (context) {
    return tokens.map((tokens, i) =>
      highlightDifferences(tokens, commonIndices[i], gapHasDiff, context),
    );
  }

  return {
    shortened: tokens.map((tokens, i) =>
      highlightDifferences(tokens, commonIndices[i], gapHasDiff, 'shortened'),
    ),
    full: tokens.map((tokens, i) =>
      highlightDifferences(tokens, commonIndices[i], gapHasDiff, 'full'),
    ),
  };
}

/**
 * Main processing function for a list of metrics.
 * Returns various representations of the metrics with differences highlighted.
 */
export function highlightDiffsInClusters(inputs: (string | null | undefined)[]): string[] {
  const inputStrings = inputs.map((input) => input ?? '');

  if (allStringEqual(inputStrings)) {
    return inputStrings;
  }

  const clusters = clusterByLCP(inputStrings.map(tokenize), 2);

  return clusters
    .map((cluster) => {
      const { commonIndices, gapHasDiff, tokens } = preprocessWithLcs(cluster);

      if (cluster.length === 1) {
        return [[startOfHighlightMark, ...tokens[0], endOfHighlightMark].join('')];
      }

      return tokens.map((tokens, i) =>
        highlightDifferences(tokens, commonIndices[i], gapHasDiff, 'full'),
      );
    })
    .flat();
}
